/**
 * Timezone defaults + per-tenant resolver.
 *
 * Drives the IANA timezone passed to date-fns-tz (toZonedTime, etc.) for
 * day-boundary logic (what "today" means for scheduling, quiz windows, etc.)
 * so it reflects the TENANT's region rather than a hardcoded 'Australia/Sydney'.
 *
 * Parallel to src/utils/locale.ts + src/utils/currency.ts. getTimezoneSync() is
 * the analogue of getLocaleSync() — a localStorage-backed accessor for pure
 * helpers + services that lack React context. TenantContext caches the value
 * on load.
 */
import type { TenantConfig } from '@/types/tenant.types';

/** Platform default timezone — Techery operates in NSW, Australia. */
export const DEFAULT_TENANT_TIMEZONE = 'Australia/Sydney';

const TIMEZONE_STORAGE_KEY = 'tenant_timezone';

/**
 * Resolve the operating timezone for a tenant. Missing tenant or missing
 * `business.timezone` returns the platform default. Prefer in React components
 * (reactive via useTenant).
 */
export function getTenantTimezone(
  tenant: TenantConfig | null | undefined,
): string {
  const override = tenant?.business?.timezone?.trim();
  return override || DEFAULT_TENANT_TIMEZONE;
}

/**
 * Synchronous timezone accessor for pure helpers + services without React
 * context — the timezone analogue of getTenantIdSync()/getLocaleSync().
 */
export function getTimezoneSync(): string {
  if (typeof window === 'undefined' || !window.localStorage) {
    return DEFAULT_TENANT_TIMEZONE;
  }
  return (
    window.localStorage.getItem(TIMEZONE_STORAGE_KEY) || DEFAULT_TENANT_TIMEZONE
  );
}

/**
 * Cache the active tenant's timezone to localStorage so getTimezoneSync() can
 * read it. Called by TenantContext whenever a tenant config loads/updates.
 */
export function cacheTenantTimezone(
  tenant: TenantConfig | null | undefined,
): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(TIMEZONE_STORAGE_KEY, getTenantTimezone(tenant));
}
